import { inject, Injectable } from '@angular/core';
import { ServiceTypeMetadataMap } from '@common/constants';
import { RetentionPolicyOdata } from '@common/models';
import { ServiceTypeToDisplayNamePipe } from '@common/pipes';
import { RetentionPolicyService } from '@common/services';
import { getNestedObjectByPath, getUniqueListByProp, RetPolicyTag, RetPolicyTagsMap } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { FilterOptions } from '@common/types';
import { getFilterByContains, getFilterByEq, getFilterByEqFromEnum } from '@common/utils/functions/search';
import { I18NextPipe } from 'angular-i18next';
import { ModelTemplate, naturalSort } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateRetentionPolicyService extends SmartSearchTemplatesBase {
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly retentionPolicyService = inject(RetentionPolicyService);
  private readonly retentionPolicy$ = this.retentionPolicyService.getPolicies();

  readonly #legalHold = RetPolicyTagsMap.get(RetPolicyTag.LegalHold);
  readonly #policyName = RetPolicyTagsMap.get(RetPolicyTag.PolicyName);
  readonly #serviceType = RetPolicyTagsMap.get(RetPolicyTag.ServiceType);

  readonly #optionYes = this.i18nPipe.transform('common.yes', { format: 'capitalize' });
  readonly #optionNo = this.i18nPipe.transform('common.no', { format: 'capitalize' });

  private readonly serviceTypeToDisplayNamePipe: ServiceTypeToDisplayNamePipe = new ServiceTypeToDisplayNamePipe();

  public readonly NameTag: ModelTemplate<RetentionPolicyOdata> = {
    tag: this.#policyName.tag,
    items: (state): Observable<RetentionPolicyOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#policyName.prop
      };

      this.retentionPolicyService.filter = term ? getFilterByContains(options) : '';

      return scheduled(
        this.retentionPolicy$.pipe(
          map((res) => res.data),
          map((policies) => {
            const filtered = policies.filter((policy) => policy.Name.toLowerCase().includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#policyName.prop).slice(0, 10);

            return uniqueList.toSorted((a, b) =>
              naturalSort(
                getNestedObjectByPath(a, this.#policyName.prop).toLowerCase(),
                getNestedObjectByPath(b, this.#policyName.prop).toLowerCase()
              )
            );
          })
        ),
        asapScheduler
      ) as Observable<RetentionPolicyOdata[]>;
    },
    itemFormatter: ({ Name }): string => Name,
    addGroupBrackets: true
  };

  public readonly TypeTag: ModelTemplate<RetentionPolicyOdata> = {
    tag: this.#serviceType.tag,
    items: (state): Observable<RetentionPolicyOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#serviceType.prop,
        enumItems: ServiceTypeMetadataMap
      };

      this.retentionPolicyService.filter = term ? getFilterByEqFromEnum(options) : '';

      return scheduled(
        this.retentionPolicy$.pipe(
          map((res) => res.data),
          map((policies) => {
            const filtered = policies.filter((policy: RetentionPolicyOdata) => policy.ServiceType.toLowerCase().includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#serviceType.prop).slice(0, 10);

            return uniqueList.toSorted((a, b) =>
              naturalSort(
                getNestedObjectByPath(a, this.#serviceType.prop).toLowerCase(),
                getNestedObjectByPath(b, this.#serviceType.prop).toLowerCase()
              )
            );
          })
        ),
        asapScheduler
      ) as Observable<RetentionPolicyOdata[]>;
    },
    itemFormatter: ({ ServiceType }): string => this.serviceTypeToDisplayNamePipe.transform(ServiceType),
    addGroupBrackets: true
  };

  public readonly LegalHoldTag: ModelTemplate<RetentionPolicyOdata> = {
    tag: this.#legalHold.tag,
    items: (state): Observable<RetentionPolicyOdata[]> => {
      const term = state.leftCaretValue;
      const options: FilterOptions = {
        model: [{ value: term }],
        prop: this.#legalHold.prop
      };

      this.retentionPolicyService.filter = term ? getFilterByEq(options, false) : '';

      return scheduled(
        this.retentionPolicy$.pipe(
          map((res) => res.data),
          map((policies) => {
            const filtered = policies.filter((policy: RetentionPolicyOdata) => String(policy.HasLegalHold).includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#legalHold.prop).slice(0, 10);

            return uniqueList.toSorted((a, b) =>
              naturalSort(
                getNestedObjectByPath(a, this.#legalHold.prop).toLowerCase(),
                getNestedObjectByPath(b, this.#legalHold.prop).toLowerCase()
              )
            );
          })
        ),
        asapScheduler
      ) as Observable<RetentionPolicyOdata[]>;
    },
    itemFormatter: ({ HasLegalHold }): string => (HasLegalHold ? this.#optionYes : this.#optionNo),
    addGroupBrackets: true
  };
}
